/**
 * InsightsFilters — date range presets + status toggle for aggregated insights.
 */
import { Download, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export type DateRangePreset = "7d" | "30d" | "90d" | "all";

export const RANGE_PRESETS: { id: DateRangePreset; label: string }[] = [
  { id: "7d", label: "7 dias" },
  { id: "30d", label: "30 dias" },
  { id: "90d", label: "90 dias" },
  { id: "all", label: "Tudo" },
];

export function rangePresetToDate(preset: DateRangePreset): Date | null {
  if (preset === "all") return null;
  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

interface InsightsFiltersProps {
  range: DateRangePreset;
  onlyDone: boolean;
  onChangeRange: (r: DateRangePreset) => void;
  onChangeOnlyDone: (v: boolean) => void;
  onExport?: () => void;
  onPrint?: () => void;
  exportDisabled?: boolean;
}

export function InsightsFilters({
  range,
  onlyDone,
  onChangeRange,
  onChangeOnlyDone,
  onExport,
  onPrint,
  exportDisabled,
}: InsightsFiltersProps) {
  return (
    <div
      data-print-hide
      className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-border bg-card/60 print:hidden"
    >
      <div className="flex items-center gap-1">
        {RANGE_PRESETS.map((p) => (
          <Button
            key={p.id}
            variant={range === p.id ? "default" : "ghost"}
            size="sm"
            className={cn(
              "h-7 px-2.5 text-xs",
              range === p.id ? "" : "text-muted-foreground",
            )}
            onClick={() => onChangeRange(p.id)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <div className="h-5 w-px bg-border/60 mx-1 hidden sm:block" />

      <div className="flex items-center gap-2">
        <Switch
          id="filter-only-done"
          checked={onlyDone}
          onCheckedChange={onChangeOnlyDone}
        />
        <Label htmlFor="filter-only-done" className="text-xs cursor-pointer">
          Apenas concluídas
        </Label>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        {onPrint && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2.5 text-xs gap-1.5 text-muted-foreground"
            onClick={onPrint}
            disabled={exportDisabled}
          >
            <Printer className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">Imprimir / PDF</span>
          </Button>
        )}
        {onExport && (
          <Button
            variant="secondary"
            size="sm"
            className="h-7 px-2.5 text-xs gap-1.5"
            onClick={onExport}
            disabled={exportDisabled}
          >
            <Download className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">Exportar CSV</span>
            <span className="sm:hidden">CSV</span>
          </Button>
        )}
      </div>
    </div>
  );
}
